import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { ensureError, isFunction } from '@fabric-app/utils/guards';
import { useQueryClient } from '@tanstack/react-query';
import equal from './node_modules/fast-deep-equal/index.js';

const upsertItemInArrayById = (data, item, options) => {
  const shouldUpsert = options?.createIfNotFound ?? true;
  let updated = false;
  const result = data.map((existingItem) => {
    if (existingItem.id === item.id) {
      updated = true;
      return options?.updateFn ? options.updateFn(existingItem) : {
        ...existingItem,
        ...item
      };
    }
    return existingItem;
  });
  if (!updated && shouldUpsert) {
    result.push(item);
  }
  return result;
};
const useOptimisticallyUpdateQueriesWithPredicate = () => {
  const queryClient = useQueryClient();
  return (predicate, callback, options) => {
    const mutate = (callback2) => {
      const currentQueriesData = queryClient.getQueriesData({
        predicate,
        ...options
      });
      for (const [key, data] of currentQueriesData) {
        const optimisticData = callback2(data, key);
        if (equal(data, optimisticData)) {
          continue;
        }
        queryClient.setQueryData(key, optimisticData);
      }
      return {
        currentQueriesData,
        resetQueriesData: () => {
          for (const [key, data] of currentQueriesData) {
            queryClient.setQueryData(key, data);
          }
        },
        invalidateQueries: () => {
          queryClient.invalidateQueries({
            predicate,
            ...options
          }).catch((e) => {
            captureException(ensureError(e), {
              level: "error"
            });
          });
        },
        remutate: mutate
      };
    };
    return mutate(callback);
  };
};
const useOptimisticallyUpdateInfiniteQueriesWithPredicate = () => {
  const queryClient = useQueryClient();
  return (args) => {
    const { predicate, options } = args;
    const mutate = (pageUpdater) => {
      const currentQueriesData = queryClient.getQueriesData({
        predicate,
        ...options
      });
      for (const [key, data] of currentQueriesData) {
        const optimisticData = data && {
          ...data,
          pages: data.pages.map((page, index) => pageUpdater(page, index, key))
        };
        if (equal(data, optimisticData)) {
          continue;
        }
        queryClient.setQueryData(key, optimisticData);
      }
      return {
        currentQueriesData,
        resetQueriesData: () => {
          for (const [key, data] of currentQueriesData) {
            queryClient.setQueryData(key, data);
          }
        },
        invalidateQueries: () => {
          queryClient.invalidateQueries({
            predicate,
            ...options
          }).catch((e) => {
            captureException(ensureError(e), {
              level: "error"
            });
          });
        },
        remutate: mutate
      };
    };
    return mutate(args.pageUpdater);
  };
};
const useOptimisticallyUpdateQueryDetailData = () => {
  const queryClient = useQueryClient();
  return (detailQueryKey, updatedItem) => {
    const currentDetailData = queryClient.getQueryData(detailQueryKey);
    const optimisticDetailData = {
      ...currentDetailData,
      ...updatedItem
    };
    queryClient.setQueryData(detailQueryKey, optimisticDetailData);
    return {
      detailQueryKey,
      currentDetailData,
      optimisticDetailData,
      resetDetailData: () => queryClient.setQueryData(detailQueryKey, currentDetailData),
      invalidateQueries: () => {
        queryClient.invalidateQueries({
          queryKey: detailQueryKey
        }).catch((e) => {
          captureException(ensureError(e), {
            level: "error"
          });
        });
      }
    };
  };
};
const withConditionalMutation = (mutation, conditionOptions) => {
  return {
    ...mutation,
    mutate: (data, options) => {
      if (conditionOptions?.ignorePredicate?.(data)) return;
      mutation.mutate(data, options);
    },
    mutateAsync: (data) => {
      if (conditionOptions?.ignorePredicate?.(data)) return Promise.resolve(void 0);
      return mutation.mutateAsync(data);
    }
  };
};
const getNextPageParam = (lastPage, pages, lastPageParam) => {
  if (lastPage.count < lastPageParam.limit * (lastPageParam.offset + 1)) {
    return void 0;
  }
  return {
    ...lastPageParam,
    offset: lastPageParam.offset + lastPageParam.limit
  };
};
const isQueryEnabled = (params) => (query) => {
  return !params.some((param) => isFunction(param) ? !param(query) : param === false);
};

export { getNextPageParam, isQueryEnabled, upsertItemInArrayById, useOptimisticallyUpdateInfiniteQueriesWithPredicate, useOptimisticallyUpdateQueriesWithPredicate, useOptimisticallyUpdateQueryDetailData, withConditionalMutation };
//# sourceMappingURL=utilities.js.map
